import React from 'react';
import { useDraftBoard } from '@/hooks/useDraftBoard';
import { DraftPickCard } from './DraftPickCard';
import { format } from 'date-fns';
import { Trophy, Calendar, Loader2 } from 'lucide-react';

export const DraftBoard: React.FC = () => {
  const { currentBoard, picks, isLoading } = useDraftBoard();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentBoard || !picks?.length) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
        <h2 className="text-xl font-bold mb-2">No Draft Board Yet</h2>
        <p className="text-muted-foreground">
          Check back next week for the top posts!
        </p>
      </div>
    );
  }

  const firstPick = picks[0];
  const topFive = picks.slice(1, 5);
  const restOfPicks = picks.slice(5);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full px-4 py-2 mb-4">
          <Trophy className="h-5 w-5 text-yellow-500" />
          <span className="font-bold text-yellow-500">Weekly Draft Board</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">Top 20 Posts</h1>
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>
            {format(new Date(currentBoard.week_start), 'MMM d')} -{' '}
            {format(new Date(currentBoard.week_end), 'MMM d, yyyy')}
          </span>
        </div>
      </div>

      {/* #1 Pick - Featured */}
      {firstPick?.post && (
        <div className="max-w-xl mx-auto">
          <h2 className="text-center text-sm font-semibold text-yellow-500 mb-3 uppercase tracking-wider">
            🏆 The #1 Overall Pick 🏆
          </h2>
          <DraftPickCard
            pickNumber={1}
            post={firstPick.post}
            totalReactions={firstPick.total_reactions}
            reactionVariety={firstPick.reaction_variety}
          />
        </div>
      )}

      {/* First Round (2-5) */}
      {topFive.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span className="text-orange-500">🔥</span> First Round Picks
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {topFive.map((pick) =>
              pick.post ? (
                <DraftPickCard
                  key={pick.id}
                  pickNumber={pick.pick_number}
                  post={pick.post}
                  totalReactions={pick.total_reactions}
                  reactionVariety={pick.reaction_variety}
                />
              ) : null
            )}
          </div>
        </div>
      )}

      {/* Rest of Draft (6-20) */}
      {restOfPicks.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span className="text-blue-500">⭐</span> Later Rounds
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {restOfPicks.map((pick) =>
              pick.post ? (
                <DraftPickCard
                  key={pick.id}
                  pickNumber={pick.pick_number}
                  post={pick.post}
                  totalReactions={pick.total_reactions}
                  reactionVariety={pick.reaction_variety}
                />
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftBoard;
